import React, { useMemo, useState } from "react";

import { MetricTiles } from "../../../components/ui";
import {
  TicketLogFooter,
  TicketLogRow,
  TicketLogTable,
} from "./TicketLogTable";

export const DEFAULT_COMPACT_TICKET_PAGE_SIZE = 10;
export const DEFAULT_COMPACT_TICKET_PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Remove write/action columns from a ticket column config for read-only summaries.
 *
 * @param {Array<Object>} columns - Full ticket table column config.
 * @param {boolean} includeActionColumn - Whether to keep action columns.
 * @returns {Array<Object>}
 */
function getReadOnlyColumns(columns, includeActionColumn) {
  if (includeActionColumn) {
    return columns;
  }
  return columns.filter((column) => column.key !== "actions" && !column.isActions);
}

/**
 * Render a compact read-only ticket log card with shared ticket pagination.
 *
 * @param {Object} props - Component props.
 * @param {string} props.title - Card title.
 * @param {string} props.subtitle - Card subtitle.
 * @param {React.ComponentType} props.Icon - Optional title icon component.
 * @param {Array<Object>} props.data - Ticket rows to display.
 * @param {Array<Object>} props.columns - Ticket table column configuration.
 * @param {React.ComponentType} props.RowComponent - Optional custom row renderer.
 * @param {(item: Object) => void} props.onRowClick - Optional row click handler.
 * @param {(item: Object) => string} [props.getRowHref] - Optional app path for middle-click / Ctrl+click new tab.
 * @param {Array<{value: string|number, label: string, accent?: string, valueClassName?: string, labelClassName?: string, className?: string}>} props.metrics
 *   Optional metric tiles shown above the table (shared ``MetricTiles`` shape).
 * @param {string} props.itemLabel - Singular item label for the footer.
 * @param {string} props.emptyMessage - Empty table message.
 * @param {Object} props.defaultSort - Default ticket table sort.
 * @param {Array<string>} props.searchFields - Fields used when search is enabled.
 * @param {Array<Object>} props.statusFilters - Status filter options.
 * @param {Function} props.statusFilterFn - Status filter predicate.
 * @param {boolean} props.showToolbar - Whether to show the compact table toolbar.
 * @param {boolean} props.showSearch - Whether to show search when toolbar is visible.
 * @param {boolean} props.showJobFilter - Whether to show job filter when toolbar is visible.
 * @param {boolean} props.showStatusFilter - Whether to show status filter when toolbar is visible.
 * @param {number} props.paginationThreshold - Item count needed before pagination appears.
 * @param {number|string} props.defaultPageSize - Initial compact page size.
 * @param {Array<number|string>} props.pageSizeOptions - Compact page-size options.
 * @param {boolean} props.includeActionColumn - Whether to preserve action columns.
 * @param {string} props.iconClassName - Icon wrapper classes.
 * @param {string} props.accentClassName - Footer accent text classes.
 * @param {string} props.selectFocusClassName - Footer select focus classes.
 * @param {React.ReactNode} props.headerAction - Optional header action content.
 * @param {React.ReactNode} props.footerEndContent - Optional footer end content.
 * @param {Object} props.rowProps - Props forwarded to the row renderer.
 * @param {boolean} props.isLoading - Whether data is loading.
 * @returns {JSX.Element}
 */
const CompactTicketSummaryCard = ({
  title,
  subtitle,
  Icon,
  data = [],
  columns = [],
  RowComponent = TicketLogRow,
  onRowClick,
  getRowHref,
  metrics = [],
  itemLabel = "ticket",
  emptyMessage = "No tickets found",
  defaultSort = { field: "updated_at", direction: "desc" },
  searchFields = ["description"],
  statusFilters = [],
  statusFilterFn,
  showToolbar = false,
  showSearch = false,
  showJobFilter = false,
  showStatusFilter = false,
  paginationThreshold = DEFAULT_COMPACT_TICKET_PAGE_SIZE,
  defaultPageSize = DEFAULT_COMPACT_TICKET_PAGE_SIZE,
  pageSizeOptions = DEFAULT_COMPACT_TICKET_PAGE_SIZE_OPTIONS,
  includeActionColumn = false,
  iconClassName = "border border-primary-100 bg-primary-50 text-primary",
  accentClassName = "text-primary",
  selectFocusClassName = "focus:border-primary focus:ring-primary",
  headerAction = null,
  footerEndContent = null,
  rowProps = {},
  isLoading = false,
}) => {
  const [filteredCount, setFilteredCount] = useState(data.length);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const readOnlyColumns = useMemo(
    () => getReadOnlyColumns(columns, includeActionColumn),
    [columns, includeActionColumn]
  );

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm shadow-gray-300">
      <div className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {Icon ? (
            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
              <Icon className="h-5 w-5" />
            </div>
          ) : null}
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-950">{title}</h2>
            {subtitle ? (
              <p className="text-sm text-gray-500">{subtitle}</p>
            ) : null}
          </div>
        </div>

        {headerAction}
      </div>

      {metrics.length > 0 ? (
        <MetricTiles
          metrics={metrics}
          className="border-b border-gray-200 bg-gray-50/60 px-3 py-2"
        />
      ) : null}

      {isLoading ? (
        <div className="flex min-h-[180px] items-center justify-center text-sm font-semibold text-gray-500">
          Loading {itemLabel}s...
        </div>
      ) : (
        <>
          <TicketLogTable
            data={data}
            columns={readOnlyColumns}
            RowComponent={RowComponent}
            onRowClick={onRowClick}
            getRowHref={getRowHref}
            searchFields={searchFields}
            statusFilters={statusFilters}
            statusFilterFn={statusFilterFn}
            onFilteredCountChange={setFilteredCount}
            onPaginationChange={setPaginationInfo}
            emptyMessage={emptyMessage}
            defaultSort={defaultSort}
            defaultView="list"
            showViewToggle={false}
            showToolbar={showToolbar}
            showSearch={showSearch}
            showJobFilter={showJobFilter}
            showStatusFilter={showStatusFilter}
            paginationThreshold={paginationThreshold}
            defaultPageSize={defaultPageSize}
            pageSizeOptions={pageSizeOptions}
            rowProps={rowProps}
          />
          <TicketLogFooter
            paginationInfo={paginationInfo}
            filteredCount={filteredCount}
            totalCount={data.length}
            itemLabel={itemLabel}
            accentClassName={accentClassName}
            selectFocusClassName={selectFocusClassName}
            endContent={footerEndContent}
          />
        </>
      )}
    </section>
  );
};

export default CompactTicketSummaryCard;
